import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  fetchTopicById, fetchModuleById, fetchSubjectById2,
  fetchSections, createSection, updateSection, deleteSection,
  fetchNotes, deleteNote,
  fetchQuestionSet,
} from "../../pages/services/notesService";
import "../../styles/CourseNotes.css";
import "../../styles/ListPage.css";

const LEGACY_QTYPES = ["example", "quiz", "practice", "assignment"];
const LEGACY_QTYPE_LABELS = {
  example: "Example Questions", quiz: "Quiz",
  practice: "Practice Questions", assignment: "Assignment Questions",
};

const SECTION_TYPES = [
  { value: "notes",      label: "Notes",                icon: "📝" },
  { value: "example",    label: "Example Questions",    icon: "💡" },
  { value: "quiz",       label: "Quiz",                 icon: "🎯" },
  { value: "practice",   label: "Practice Questions",   icon: "📚" },
  { value: "assignment", label: "Assignment Questions",  icon: "📋" },
];

const typeLabel = (type) => SECTION_TYPES.find((t) => t.value === type)?.label || type;
const typeIcon  = (type) => SECTION_TYPES.find((t) => t.value === type)?.icon || "";

const TYPE_COLORS = {
  notes:      { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
  example:    { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa" },
  quiz:       { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" },
  practice:   { bg: "#fdf4ff", color: "#7e22ce", border: "#e9d5ff" },
  assignment: { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" },
};

const TypeBadge = ({ type }) => {
  const style = TYPE_COLORS[type] || {};
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600,
      background: style.bg, color: style.color, border: `1px solid ${style.border}`,
    }}>
      {typeIcon(type)} {typeLabel(type)}
    </span>
  );
};

const TopicPage = () => {
  const { subjectId, topicId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [topicTitle,   setTopicTitle]   = useState("");
  const [moduleTitle,  setModuleTitle]  = useState("");
  const [subjectTitle, setSubjectTitle] = useState("");
  const [sections,     setSections]     = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [toast,        setToast]        = useState("");

  // Legacy content (notes/questions added to the topic directly before sections existed)
  const [legacyNotes,    setLegacyNotes]    = useState([]);
  const [legacyQSets,    setLegacyQSets]    = useState({}); // { type: questionSet }
  const [legacyExpanded, setLegacyExpanded] = useState(false);

  // Add/Edit modal state
  const [modalOpen,      setModalOpen]      = useState(false);
  const [modalMode,      setModalMode]      = useState("add"); // "add" | "edit"
  const [editingSection, setEditingSection] = useState(null);
  const [modalTitle,     setModalTitle]     = useState("");
  const [modalType,      setModalType]      = useState("notes");
  const [modalSaving,    setModalSaving]    = useState(false);
  const [modalError,     setModalError]     = useState("");

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  useEffect(() => {
    if (location.state?.successMessage) {
      showToast(location.state.successMessage);
      window.history.replaceState({}, "");
    }
  }, []);

  useEffect(() => {
    fetchTopicById(topicId).then((t) => {
      setTopicTitle(t?.title || "Topic");
      if (t?.moduleId) fetchModuleById(t.moduleId).then((m) => setModuleTitle(m?.title || "")).catch(() => {});
    }).catch(() => {});
    fetchSubjectById2(subjectId).then((s) => setSubjectTitle(s?.title || "")).catch(() => {});
    loadSections();
    loadLegacyContent();
  }, [topicId, subjectId]);

  const loadLegacyContent = async () => {
    // Fetch notes that were created directly under this topic (before sections)
    fetchNotes(topicId).then((n) => setLegacyNotes(n || [])).catch(() => setLegacyNotes([]));
    // Fetch question sets that were created directly under this topic
    const sets = {};
    await Promise.all(
      LEGACY_QTYPES.map(type =>
        fetchQuestionSet(topicId, type)
          .then(qs => { if ((qs?.questions || []).length > 0) sets[type] = qs; })
          .catch(() => {})
      )
    );
    setLegacyQSets(sets);
  };

  const handleDeleteLegacyNote = async (noteId) => {
    if (!window.confirm("Delete this note? This cannot be undone.")) return;
    try {
      await deleteNote(topicId, noteId);
      setLegacyNotes(prev => prev.filter(n => n._id !== noteId));
      showToast("Note deleted");
    } catch { showToast("Error deleting note"); }
  };

  const loadSections = async () => {
    setLoading(true);
    try { setSections(await fetchSections(topicId)); } catch { setSections([]); }
    setLoading(false);
  };

  const openAddModal = () => {
    setModalMode("add");
    setEditingSection(null);
    setModalTitle("");
    setModalType("notes");
    setModalError("");
    setModalOpen(true);
  };

  const openEditModal = (section) => {
    setModalMode("edit");
    setEditingSection(section);
    setModalTitle(section.title);
    setModalType(section.type);
    setModalError("");
    setModalOpen(true);
  };

  const closeModal = () => { setModalOpen(false); setModalSaving(false); };

  const handleModalSave = async () => {
    if (!modalTitle.trim()) { setModalError("Title is required"); return; }
    setModalSaving(true);
    setModalError("");
    try {
      if (modalMode === "add") {
        await createSection(topicId, { title: modalTitle.trim(), type: modalType });
        showToast("Section created");
      } else {
        await updateSection(editingSection._id, { title: modalTitle.trim() });
        showToast("Section updated");
      }
      closeModal();
      loadSections();
    } catch {
      setModalError("Failed to save. Please try again.");
    }
    setModalSaving(false);
  };

  const handleDelete = async (section) => {
    if (!window.confirm(`Delete section "${section.title}"? This cannot be undone.`)) return;
    try {
      await deleteSection(section._id);
      showToast("Section deleted");
      loadSections();
    } catch { showToast("Error deleting section"); }
  };

  return (
    <div className="cn-page">
      {toast && <div className="cn-toast">{toast}</div>}

      {/* Breadcrumb */}
      <div className="cn-breadcrumb">
        <span className="cn-breadcrumb-link" onClick={() => navigate("/notes")}>Notes</span>
        {subjectTitle && (
          <>
            <span className="cn-breadcrumb-sep"> › </span>
            <span className="cn-breadcrumb-link" onClick={() => navigate(`/notes/subjects/${subjectId}`)}>
              Subject: {subjectTitle}
            </span>
          </>
        )}
        {moduleTitle && (
          <>
            <span className="cn-breadcrumb-sep"> › </span>
            <span className="cn-breadcrumb-link" onClick={() => navigate(`/notes/subjects/${subjectId}`)}>
              Module: {moduleTitle}
            </span>
          </>
        )}
        <span className="cn-breadcrumb-sep"> › </span>
        <span className="cn-breadcrumb-current">Topic: {topicTitle}</span>
      </div>

      <div className="cn-header">
        <div>
          <h2 className="cn-title">{topicTitle}</h2>
          <p style={{ margin: 0, fontSize: 13, color: "#64748b" }}>
            Sections define the type of content in each part of this topic
          </p>
        </div>
        <button className="cn-back-btn" onClick={() => navigate(`/notes/subjects/${subjectId}`)}>
          ← Back
        </button>
      </div>

      <div className="cn-tab-toolbar" style={{ marginTop: 20 }}>
        <span className="cn-tab-count">
          {sections.length} section{sections.length !== 1 ? "s" : ""}
        </span>
        <button className="cn-primary-btn" onClick={openAddModal}>
          + Add Section
        </button>
      </div>

      {loading ? (
        <div className="cn-loading">Loading...</div>
      ) : sections.length === 0 ? (
        <p className="cn-empty">No sections yet. Add one above to get started.</p>
      ) : (
        <div className="cn-notes-list" style={{ marginTop: 12 }}>
          {sections.map((section, i) => (
            <div key={section._id} className="cn-note-row" style={{ alignItems: "center", gap: 12 }}>
              <span style={{ color: "#94a3b8", fontWeight: 600, minWidth: 24, fontSize: 13 }}>{i + 1}.</span>
              <div className="cn-note-info" style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <span className="cn-note-title">{section.title}</span>
                  <TypeBadge type={section.type} />
                </div>
              </div>
              <div className="cn-note-actions">
                <button
                  className="cn-view-btn"
                  onClick={() =>
                    navigate(`/notes/subjects/${subjectId}/topics/${topicId}/sections/${section._id}`)
                  }
                >
                  Manage
                </button>
                <button className="cn-edit-btn" onClick={() => openEditModal(section)}>
                  Rename
                </button>
                <button className="cn-del-btn" onClick={() => handleDelete(section)}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Legacy Content (added before sections were introduced) ── */}
      {(legacyNotes.length > 0 || Object.keys(legacyQSets).length > 0) && (
        <div style={{ marginTop: 32, borderTop: "2px dashed #fbbf24", paddingTop: 20 }}>
          <div
            style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", userSelect: "none" }}
            onClick={() => setLegacyExpanded(v => !v)}
          >
            <span style={{ fontSize: 18 }}>⚠️</span>
            <div>
              <span style={{ fontWeight: 700, color: "#92400e", fontSize: 14 }}>
                Legacy content ({legacyNotes.length + Object.values(legacyQSets).reduce((s, qs) => s + (qs?.questions?.length || 0), 0)} items)
              </span>
              <p style={{ margin: 0, fontSize: 12, color: "#b45309" }}>
                This content was added to the topic directly before sections were introduced. It is still in the database — nothing was deleted. Click to expand and manage it.
              </p>
            </div>
            <span style={{ marginLeft: "auto", color: "#b45309", fontSize: 18 }}>
              {legacyExpanded ? "▲" : "▼"}
            </span>
          </div>

          {legacyExpanded && (
            <div style={{ marginTop: 16 }}>
              {/* Legacy Notes */}
              {legacyNotes.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <h4 style={{ fontSize: 13, fontWeight: 700, color: "#64748b", margin: "0 0 8px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Notes ({legacyNotes.length})
                  </h4>
                  <div className="cn-notes-list">
                    {legacyNotes.map(note => (
                      <div key={note._id} className="cn-note-row">
                        <div className="cn-note-info">
                          <span className="cn-note-title">{note.title}</span>
                        </div>
                        <div className="cn-note-actions">
                          <button
                            className="cn-edit-btn"
                            onClick={() =>
                              navigate(`/notes/subjects/${subjectId}/topics/${topicId}/notes/${note._id}/edit`)
                            }
                          >
                            Edit
                          </button>
                          <button className="cn-del-btn" onClick={() => handleDeleteLegacyNote(note._id)}>
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Legacy Question Sets */}
              {Object.entries(legacyQSets).map(([type, qs]) => (
                <div key={type} style={{ marginBottom: 16 }}>
                  <h4 style={{ fontSize: 13, fontWeight: 700, color: "#64748b", margin: "0 0 8px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    {LEGACY_QTYPE_LABELS[type]} ({(qs?.questions || []).length} questions)
                  </h4>
                  <div className="cn-added-qs">
                    {(qs?.questions || []).map((q, i) => (
                      <div key={q._id} className="cn-q-row cn-q-added" style={{ opacity: 0.8 }}>
                        <div className="cn-q-info">
                          <div style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
                            <span className="cn-q-num">{i + 1}.</span>
                            <span className="cn-q-text">{q.question}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <div style={{ padding: "12px 14px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, fontSize: 13, color: "#92400e", marginTop: 8 }}>
                <strong>To preserve this content:</strong> Create a matching section above (e.g. a "Notes" section for the notes, a "Quiz" section for quiz questions) and re-add the content there. You can then delete these legacy items once migrated.
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add / Edit Section Modal */}
      {modalOpen && (
        <div className="cn-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="cn-modal">
            <div className="cn-modal-header">
              <h3 className="cn-modal-title">{modalMode === "add" ? "Add Section" : "Rename Section"}</h3>
              <button className="cn-modal-close" onClick={closeModal}>✕</button>
            </div>

            <div className="cn-modal-body">
              {modalError && <div className="cn-error" style={{ marginBottom: 12 }}>{modalError}</div>}

              <div className="cn-form-field">
                <label className="cn-form-label">Section Title</label>
                <input
                  className="cn-form-input"
                  placeholder="e.g. Introduction Notes, Practice Set 1…"
                  value={modalTitle}
                  onChange={(e) => setModalTitle(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleModalSave()}
                  autoFocus
                />
              </div>

              {modalMode === "add" && (
                <div className="cn-form-field" style={{ marginTop: 16 }}>
                  <label className="cn-form-label">Section Type</label>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6 }}>
                    {SECTION_TYPES.map((t) => {
                      const active = modalType === t.value;
                      const style = TYPE_COLORS[t.value] || {};
                      return (
                        <button
                          key={t.value}
                          onClick={() => setModalType(t.value)}
                          style={{
                            display: "flex", alignItems: "center", gap: 6,
                            padding: "8px 14px", borderRadius: 8, cursor: "pointer",
                            fontSize: 13, fontWeight: active ? 700 : 500,
                            border: `2px solid ${active ? style.color : style.border}`,
                            background: active ? style.bg : "#fff",
                            color: active ? style.color : "#64748b",
                            transition: "all 0.15s",
                          }}
                        >
                          {t.icon} {t.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="cn-modal-footer">
              <button className="cn-cancel-btn-lg" onClick={closeModal} disabled={modalSaving}>
                Cancel
              </button>
              <button className="cn-save-btn-lg" onClick={handleModalSave} disabled={modalSaving}>
                {modalSaving ? "Saving..." : modalMode === "add" ? "Create Section" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopicPage;
